import { useState } from "react";
import "./NewMessage.css";
import { v4 as uuidv4 } from "uuid";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { API, graphqlOperation } from "aws-amplify";
import * as mutations from "../../graphql/mutations";
import { Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { InputForm } from "../InputForm/InputForm";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { MessageType } from "./NewMessageType";
import { Checkbox } from "../Checkbox/Checkbox";
import { Notification } from "../Notification/Notification";

export const NewMessage = () => {
  const [message, setMessage] = useState({
    id: uuidv4(),
    Title: "",
    Headline: "",
    Summary: "",
    Description: "",
    StartDate: new Date().toISOString(),
    EndDate: new Date().toISOString(),
    Active: false,
    TMCUsers: false,
    TIUsers: false,
    Renewals: false,
    Marketing: false,
    MatchAll: false,
  } as MessageType);
  const [startDate, setStartDate] = useState(new Date() as Date);
  const [endDate, setEndDate] = useState(new Date() as Date);
  const navigate = useNavigate();
  const timeout: number = 4000;
  const [toastTitle, setToastTitle] = useState("" as string);
  const [toastDescription, setToastDescription] = useState("" as string);
  const [showToast, setShowToast] = useState(false as boolean);

  const goBack = () => {
    navigate(-1);
  };

  const quillModules = {
    toolbar: [
      [
        {
          font: [],
        },
      ],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ["bold", "italic", "link"],
      [{ list: "ordered" }, { list: "bulleted" }],
      [{ indent: -1 }, { indent: 1 }, { align: [] }],
      [],
    ],
  };

  const handleSubmitMessage = async () => {
    const id = uuidv4();
    try {
      await API.graphql(
        graphqlOperation(mutations.createMessages, {
          messages: {
            id,
            ...message,
          },
        })
      );
      setShowToast(true);
      setToastTitle("Message Saved");
      setToastDescription(
        `Message with title: ${message?.Title} has been saved`
      );
      setTimeout(() => {
        navigate(-1);
      }, timeout);
    } catch (e) {
      console.log(e);
    }
  };

  const handleChangeText = (value: string, name: string) => {
    const strippedValue = stripHTML(value);
    console.log("looking at stripped value " + strippedValue);
    setMessage((message) => {
      return {
        ...message,
        [name]: strippedValue,
      };
    });
  };

  const handleDescription = (value: string) => {
    setMessage((message) => {
      return {
        ...message,
        Description: value,
      };
    });
  };

  const handleStartDate = (value: Date) => {
    if (value) {
      setStartDate(value);
      let convertedStartDate = new Date(value);
      const startDateISOString = convertedStartDate.toISOString();
      setMessage((message: MessageType) => {
        return {
          ...message,
          StartDate: startDateISOString,
        };
      });
    }
  };

  const handleEndDate = (value: Date) => {
    setEndDate(value);
    const convertedEndDate = new Date(value);
    const endDateISOString = convertedEndDate.toISOString();
    setMessage((message: MessageType) => {
      return {
        ...message,
        EndDate: endDateISOString,
      };
    });
  };

  const handleSelectedEndDate = () => {
    if (startDate > endDate) {
      return startDate;
    } else {
      return endDate;
    }
  };

  const handleCheckValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    const name = event.target.name;
    setMessage((message) => {
      return {
        ...message,
        [name]: checked,
      };
    });
  };

  const handleNotificationClose = () => {
    setShowToast(false);
  };

  const disableButton = () => {
    if (
      message?.Title?.length === 0 ||
      message?.Headline?.length === 0 ||
      message?.StartDate?.length === 0
    ) {
      return true;
    } else {
      return false;
    }
  };

  const stripHTML = (html) => {
    console.log("trying to strip");
    let doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  };

  return (
    <div className="page-container">
      <h1 className="title">New Message</h1>
      <div className="editor-new-message">
        <div>
          <InputForm
            name="Title"
            ariaLabel="Title"
            size="lg"
            onChange={handleChangeText}
            labelText="Title"
            controlId="formMessages"
            required={true}
          />
        </div>
        <div>
          <InputForm
            name="Headline"
            ariaLabel="Headline"
            required={true}
            size="lg"
            onChange={handleChangeText}
            labelText="Headline"
            controlId="formMessages"
          />
        </div>
        <div>
          <InputForm
            name="Summary"
            ariaLabel="Summary"
            size="lg"
            onChange={handleChangeText}
            labelText="Summary"
            controlId="formMessages"
          />
        </div>
        <div className="description-wrapper">
          <div className="description-title">Description</div>
          <ReactQuill
            modules={quillModules}
            theme="snow"
            className="description"
            onChange={handleDescription}
          />
        </div>
        <div>
          <span>
            Start Date:
            <span className="required-label">*</span>
            <DatePicker
              selected={startDate}
              onChange={(date: Date) => handleStartDate(date)}
              showMonthPicker
              showTimeInput
              showMonthDropdown
              showYearDropdown
              timeInputLabel="Time:"
              dateFormat="dd/MM/yyyy h:mm aa"
            />
          </span>
        </div>
        <div>
          <span>
            End Date:
            <DatePicker
              selected={handleSelectedEndDate()}
              onChange={(date: Date) => handleEndDate(date)}
              minDate={startDate}
              showMonthPicker
              showTimeInput
              showMonthDropdown
              showYearDropdown
              timeInputLabel="Time:"
              dateFormat="dd/MM/yyyy h:mm aa"
            />
          </span>
        </div>
        <div>
          <span className="audience-title">Target Audience</span>
        </div>
        <div>
          <span>
            <Checkbox
              label="Is Active"
              name="Active"
              handleCheck={handleCheckValue}
            />
          </span>
        </div>
        <div>
          <span>
            <Checkbox
              label="TMC User"
              name="TMCUsers"
              handleCheck={handleCheckValue}
            />
          </span>
        </div>
        <div>
          <span>
            <Checkbox
              label="TI User"
              name="TIUsers"
              handleCheck={handleCheckValue}
            />
          </span>
        </div>
        <div>
          <span>
            <Checkbox
              label="Users with Upcoming Renewals"
              name="Renewals"
              handleCheck={handleCheckValue}
            />
          </span>
        </div>
        <div>
          <span>
            <Checkbox
              name="Marketing"
              label="Match All Audiences"
              handleCheck={handleCheckValue}
            />
          </span>
        </div>
        <div>
          <span>
            <Checkbox
              name="MatchAll"
              label="Signed up to Marketing?"
              handleCheck={handleCheckValue}
            />
          </span>
        </div>
        <div className="button-wrapper-new">
          <Button
            type="button"
            className="btn btn-danger save-button"
            disabled={disableButton()}
            onClick={handleSubmitMessage}
          >
            Save
          </Button>
          <Button type="button" className="btn btn-secondary" onClick={goBack}>
            Cancel
          </Button>
        </div>
        <div className="notification-wrapper">
          <Notification
            show={showToast}
            timer={timeout}
            position="bottom-end"
            title={toastTitle}
            description={toastDescription}
            autohide={true}
            handleClose={handleNotificationClose}
          />
        </div>
      </div>
    </div>
  );
};
